import { useState, type JSX } from 'react';

import { invitationFallbackReason, type InvitationEmailDelivery } from '@/lib/invitation-email';

import styles from './InvitationResultDialog.module.scss';
import { Modal } from './Modal';

interface Props {
  email: string;
  delivery: InvitationEmailDelivery;
  /** Link de un solo uso (respaldo); `null` si el email salió o el API no lo devolvió. */
  link: string | null;
  /** ISO; `null` si no se pudo emitir. */
  expiresAt: string | null;
  /** Nombre de la acción para pedir un link nuevo ("Renovar invitación", "Reenviar invitación"). */
  resendAction: string;
  /** Qué hace el link al abrirlo (p. ej. "definir su contraseña"). */
  purpose?: string;
  onClose: () => void;
}

function formatExpiry(iso: string): string {
  return new Date(iso).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * OS-1717 / OS-1719 — resultado real de una invitación por email. Con el email confirmado por el
 * proveedor no muestra ningún link. Si falló, no se confirmó o no está configurado, muestra el
 * link de un solo uso como respaldo (una sola vez: el API solo guarda su hash). Sin cierre por
 * Esc ni clic afuera: solo se cierra con el botón.
 */
export function InvitationResultDialog({
  email,
  delivery,
  link,
  expiresAt,
  resendAction,
  purpose,
  onClose,
}: Props): JSX.Element {
  const [copied, setCopied] = useState(false);
  const expires = expiresAt ? formatExpiry(expiresAt) : null;

  async function copy(value: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  if (delivery.status === 'SENT') {
    return (
      <Modal
        title="Invitación enviada"
        footer={
          <button type="button" className={styles.primaryBtn} onClick={onClose} data-autofocus>
            Listo
          </button>
        }
      >
        <div className={styles.body}>
          <p role="status">
            Enviamos la invitación a <strong>{email}</strong>
            {purpose ? ` para ${purpose}` : ''}. El link es de un solo uso
            {expires ? ` (vence el ${expires})` : ''}.
          </p>
          <p className={styles.muted}>
            Si no le llega, usa "{resendAction}": genera un link nuevo y deja sin efecto el anterior.
          </p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title="Invitación creada: comparte el link"
      footer={
        <button type="button" className={styles.primaryBtn} onClick={onClose} data-autofocus>
          {link ? 'Listo, ya lo compartí' : 'Cerrar'}
        </button>
      }
    >
      <div className={styles.body}>
        <p className={styles.error} role="alert">
          {invitationFallbackReason(delivery.status, delivery.willRetry)}
        </p>
        {link ? (
          <>
            <p>
              Comparte este link con <strong>{email}</strong> por un canal seguro. Es de un solo uso
              {expires ? `, vence el ${expires}` : ''} y <strong>no se volverá a mostrar</strong>.
            </p>
            <div className={styles.linkBox}>
              <code className={styles.linkText}>{link}</code>
              <button type="button" className={styles.secondaryBtn} onClick={() => void copy(link)}>
                {copied ? 'Copiado' : 'Copiar link'}
              </button>
            </div>
            <p className={styles.muted} aria-live="polite">
              {copied ? 'Link copiado al portapapeles.' : `Si se pierde, usa "${resendAction}".`}
            </p>
          </>
        ) : (
          <p>No recibimos el link de respaldo. Usa "{resendAction}" para generar uno nuevo.</p>
        )}
      </div>
    </Modal>
  );
}
